import { useEffect, useState } from "react";
import { collection, query, where, onSnapshot, doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useUser } from "@/lib/useUser";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import {
  Building2, TrendingUp, DollarSign, FileText, FolderOpen,
  Calendar as CalendarIcon, Video, MessageSquare,
  Flame, Filter, BarChart3, AlertTriangle, Monitor,
  Users, ChevronRight, Lock, PenLine
} from "lucide-react";
import { DealMeetingsSection } from "@/components/meetings/DealMeetingsSection";
import { MyOffersSection } from "@/components/lender/MyOffersSection";
import { OnboardingGuide } from "@/components/onboarding/OnboardingGuide";
import { LenderDealPreScreen } from "@/components/lender/LenderDealPreScreen";

// ── Palette tokens ─────────────────────────────────────────────────────────
const NAVY = "#1D314D";
const GOLD  = "#CEAB65";

interface LoanStats {
  totalOffers: number;
  activeLoans: number;
  totalAmount: number;
  pendingReviews: number;
}

// Correspond aux documents de la collection `financingOpportunities`,
// créés par LenderMatchingService.onDealApprovalPaid() une fois le
// vendeur/courtier a payé les 2 449 $ de publication.
interface Opportunity {
  id: string; // id du document financingOpportunities
  dealId: string;
  dealName: string;
  industry: string;
  isByod: boolean;
  status: string; // 'new' | 'viewed' | 'offer_created' | 'offer_sent'
  ndaRequired: boolean;
  ndaSigned: boolean;
  accessGranted: boolean;
  financials?: {
    askingPrice?: number;
    revenue?: number;
    ebitda?: number;
    cashFlow?: number;
  };
  createdAt: string;
  // Enrichissement optionnel depuis financialAnalyses/{dealId}
  ebitdaMultiple?: number;
  debtServiceCoverageRatio?: number;
  isNew?: boolean;
}

const SECTORS = [
  "all", "Technology", "Healthcare", "Manufacturing",
  "Retail", "Food & Beverage", "Construction",
  "Professional Services", "Transportation", "Finance", "Real Estate",
];

// ── Translations ────────────────────────────────────────────────────────────
const T = {
  en: {
    title: "Lender Dashboard", subtitle: "Manage your loan offers and portfolio",
    totalOpportunities: "Opportunities", availableDeals: "Available deals",
    activeLoans: "Active Loans", currentlyFunded: "Currently funded",
    totalPortfolio: "Total Portfolio", totalDealValue: "Total deal value",
    pendingReviews: "Pending Reviews", awaitingReview: "Awaiting review",
    recentOpportunities: "Recent Financing Opportunities",
    latestDeals: "Latest deals seeking financing",
    noOpportunities: "No opportunities available at the moment",
    askingPrice: "Asking Price", ebitda: "EBITDA", multiple: "Multiple",
    reviewSubmit: "Review & Submit", quickActions: "Quick Actions",
    browseDeals: "Browse Deals", calendar: "Calendar", documents: "Documents",
    messages: "Messages", pipeline: "Pipeline", submissions: "Submissions",
    allSectors: "All Sectors", filterBySector: "Filter by sector",
    newBadge: "NEW", viewAllOpportunities: "View All",
    submitOffer: "Submit Offer", recentMeetings: "Upcoming Meetings",
    meetingsDesc: "Scheduled deal meetings", dealMonitor: "Deal Monitor",
    consultants: "Consultants", videoConf: "Video",
    signNda: "Sign NDA", ndaRequiredBadge: "NDA Required",
  },
  fr: {
    title: "Tableau de bord Prêteur", subtitle: "Gérez vos offres de prêt et votre portefeuille",
    totalOpportunities: "Opportunités", availableDeals: "Deals disponibles",
    activeLoans: "Prêts Actifs", currentlyFunded: "Actuellement financés",
    totalPortfolio: "Portefeuille Total", totalDealValue: "Valeur totale des deals",
    pendingReviews: "Révisions en Attente", awaitingReview: "En attente de révision",
    recentOpportunities: "Opportunités de Financement Récentes",
    latestDeals: "Derniers deals cherchant du financement",
    noOpportunities: "Aucune opportunité disponible pour le moment",
    askingPrice: "Prix demandé", ebitda: "EBITDA", multiple: "Multiple",
    reviewSubmit: "Réviser & Soumettre", quickActions: "Actions Rapides",
    browseDeals: "Voir les Deals", calendar: "Calendrier", documents: "Documents",
    messages: "Messages", pipeline: "Pipeline", submissions: "Soumissions",
    allSectors: "Tous les Secteurs", filterBySector: "Filtrer par secteur",
    newBadge: "NOUVEAU", viewAllOpportunities: "Voir tout",
    submitOffer: "Soumettre une Offre", recentMeetings: "Réunions à venir",
    meetingsDesc: "Réunions planifiées", dealMonitor: "Deal Monitor",
    consultants: "Consultants", videoConf: "Vidéo",
    signNda: "Signer le NDA", ndaRequiredBadge: "NDA Requis",
  },
};

// ── Sub-components ──────────────────────────────────────────────────────────
function StatCard({
  label, value, sub, icon: Icon, accent,
}: {
  label: string; value: string | number; sub: string;
  icon: React.ElementType; accent: string;
}) {
  return (
    <div
      className="bg-card rounded-lg p-4 relative overflow-hidden"
      style={{ border: "0.5px solid var(--color-border-tertiary)", borderTop: `2.5px solid ${accent}` }}
    >
      <div className="flex items-start justify-between mb-3">
        <p className="text-[10.5px] uppercase tracking-wide text-muted-foreground">{label}</p>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <p className="text-2xl font-medium tracking-tight" style={{ color: "var(--color-text-primary)" }}>{value}</p>
      <p className="text-[10px] text-muted-foreground mt-1">{sub}</p>
    </div>
  );
}

function DscrBadge({ dscr }: { dscr: number }) {
  if (!dscr) return null;
  if (dscr >= 1.25) return (
    <span className="inline-flex items-center text-[9px] font-medium px-1.5 py-0.5 rounded"
          style={{ background: "rgba(99,153,34,0.1)", color: "#3B6D11", border: "0.5px solid rgba(99,153,34,0.2)" }}>
      DSCR {dscr.toFixed(2)}× ✓
    </span>
  );
  if (dscr >= 1.0) return (
    <span className="inline-flex items-center text-[9px] font-medium px-1.5 py-0.5 rounded"
          style={{ background: "rgba(186,117,23,0.1)", color: "#854F0B", border: "0.5px solid rgba(186,117,23,0.2)" }}>
      DSCR {dscr.toFixed(2)}× ⚠
    </span>
  );
  return (
    <span className="inline-flex items-center text-[9px] font-medium px-1.5 py-0.5 rounded"
          style={{ background: "rgba(162,45,45,0.1)", color: "#791F1F", border: "0.5px solid rgba(162,45,45,0.2)" }}>
      DSCR {dscr.toFixed(2)}× ✗
    </span>
  );
}

function QuickActionCard({
  icon: Icon, label, onClick,
}: {
  icon: React.ElementType; label: string; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-2 p-3 rounded-lg transition-all duration-150 group"
      style={{ border: "0.5px solid var(--color-border-tertiary)", background: "var(--color-background-primary)" }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = "rgba(29,49,77,0.3)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = "var(--color-border-tertiary)";
      }}
    >
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-150"
        style={{ background: "rgba(29,49,77,0.06)" }}
      >
        <Icon className="w-4.5 h-4.5 transition-colors duration-150 group-hover:text-[#1D314D]"
              style={{ color: "var(--color-text-secondary)" }} />
      </div>
      <span className="text-[10.5px] text-muted-foreground text-center leading-tight">{label}</span>
    </button>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export default function LenderIndex() {
  const { user, role } = useUser();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = T[language as "en" | "fr"] ?? T.en;

  const [stats, setStats] = useState<LoanStats>({ totalOffers: 0, activeLoans: 0, totalAmount: 0, pendingReviews: 0 });
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [sectorFilter, setSectorFilter] = useState("all");
  const [preScreenDealId, setPreScreenDealId] = useState<string | null>(null);

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);

  // Charge les opportunités de financement réellement matchées pour ce
  // prêteur (créées par LenderMatchingService.onDealApprovalPaid()),
  // au lieu de l'ancienne requête sur `deals` (status "pending-financing",
  // qui n'est jamais écrit nulle part dans le pipeline actuel).
  useEffect(() => {
    if (!user || !["lender", "admin", "staff"].includes(role)) { setLoading(false); return; }

    const q = query(collection(db, "financingOpportunities"), where("lenderId", "==", user.id));
    const unsub = onSnapshot(q, async (snap) => {
      const opps = snap.docs.map(d => ({ id: d.id, ...d.data() })) as Opportunity[];
      opps.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());

      const enriched = await Promise.all(
        opps.slice(0, 20).map(async (opp) => {
          const isNew = Date.now() - new Date(opp.createdAt || 0).getTime() < 48 * 3600_000;
          try {
            const fa = await getDoc(doc(db, "financialAnalyses", opp.dealId));
            if (fa.exists()) {
              const d = fa.data() as any;
              return { ...opp, ebitdaMultiple: d.ebitdaMultiple, debtServiceCoverageRatio: d.debtServiceCoverageRatio, isNew };
            }
          } catch {}
          return { ...opp, isNew };
        })
      );

      setOpportunities(enriched);
      setStats({
        totalOffers: opps.length,
        activeLoans: opps.filter(o => o.status === 'offer_sent').length,
        totalAmount: opps.reduce((s, o) => s + (o.financials?.askingPrice ?? 0), 0),
        pendingReviews: opps.filter(o => o.ndaSigned && o.status === 'new').length,
      });
      setLoading(false);
    }, () => { setOpportunities([]); setLoading(false); });

    return () => unsub();
  }, [user, role]);

  const filteredOpportunities = sectorFilter === "all"
    ? opportunities
    : opportunities.filter(o => o.industry?.toLowerCase().includes(sectorFilter.toLowerCase()));

  const handleSignNda = (opp: Opportunity) => {
    const params = new URLSearchParams({
      deal: opp.dealId,
      dealName: opp.dealName || "",
      industry: opp.industry || "",
      recipientType: "lender",
    });
    navigate(`/tools/nda-generator?${params.toString()}`);
  };

  // ── Guards ──────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="animate-spin rounded-full h-7 w-7" style={{ borderBottom: `2px solid ${NAVY}` }} />
    </div>
  );

  if (preScreenDealId) return (
    <LenderDealPreScreen
      dealId={preScreenDealId}
      language={language as "en" | "fr"}
      onProceed={() => { navigate(`/lender/submit-offer?dealId=${preScreenDealId}`); setPreScreenDealId(null); }}
      onBack={() => setPreScreenDealId(null)}
    />
  );

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      <OnboardingGuide role="lender" language={language as "en" | "fr"} />

      {/* Header row */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-medium tracking-tight">{t.title}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{t.subtitle}</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={() => navigate("/dashboard/lender/loan-offers")}>
            {t.viewAllOpportunities}
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
        <StatCard label={t.totalOpportunities} value={stats.totalOffers}  sub={t.availableDeals}  icon={Building2}     accent={GOLD} />
        <StatCard label={t.activeLoans}         value={stats.activeLoans} sub={t.currentlyFunded} icon={TrendingUp}    accent="#639922" />
        <StatCard label={t.totalPortfolio}      value={formatCurrency(stats.totalAmount)} sub={t.totalDealValue} icon={DollarSign} accent={NAVY} />
        <StatCard label={t.pendingReviews}      value={stats.pendingReviews} sub={t.awaitingReview} icon={AlertTriangle} accent="#BA7517" />
      </div>

      {/* Opportunities */}
      <Card style={{ border: "0.5px solid var(--color-border-tertiary)" }}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: GOLD }} />
                {t.recentOpportunities}
              </CardTitle>
              <CardDescription className="text-xs mt-0.5">{t.latestDeals}</CardDescription>
            </div>
            <Select value={sectorFilter} onValueChange={setSectorFilter}>
              <SelectTrigger className="w-[180px] h-8 text-xs">
                <Filter className="h-3 w-3 mr-1.5 text-muted-foreground" />
                <SelectValue placeholder={t.filterBySector} />
              </SelectTrigger>
              <SelectContent>
                {SECTORS.map(s => (
                  <SelectItem key={s} value={s} className="text-xs">
                    {s === "all" ? t.allSectors : s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {filteredOpportunities.length === 0 ? (
            <p className="text-center py-8 text-sm text-muted-foreground">{t.noOpportunities}</p>
          ) : (
            <div className="divide-y divide-border">
              {filteredOpportunities.map((opp) => (
                <div key={opp.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                  {/* Accent bar */}
                  <div
                    className="w-0.5 h-10 rounded-full flex-shrink-0"
                    style={{ background: opp.isNew ? GOLD : "rgba(29,49,77,0.18)" }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-medium text-sm">{opp.dealName || "—"}</span>
                      {opp.isNew && (
                        <span
                          className="inline-flex items-center gap-0.5 text-[9px] font-medium px-1.5 py-0.5 rounded"
                          style={{ background: "rgba(206,171,101,0.12)", color: "#854F0B", border: "0.5px solid rgba(206,171,101,0.3)" }}
                        >
                          <Flame className="w-2.5 h-2.5" /> {t.newBadge}
                        </span>
                      )}
                      {!opp.ndaSigned && (
                        <span
                          className="inline-flex items-center gap-0.5 text-[9px] font-medium px-1.5 py-0.5 rounded"
                          style={{ background: "rgba(162,45,45,0.08)", color: "#791F1F", border: "0.5px solid rgba(162,45,45,0.2)" }}
                        >
                          <Lock className="w-2.5 h-2.5" /> {t.ndaRequiredBadge}
                        </span>
                      )}
                      <DscrBadge dscr={opp.debtServiceCoverageRatio ?? 0} />
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {opp.industry ?? "—"}
                    </p>
                    {opp.ndaSigned && (
                      <div className="flex gap-3 mt-1 text-[11px] text-muted-foreground">
                        {opp.financials?.ebitda ? <span>{t.ebitda}: <strong className="text-foreground">{formatCurrency(opp.financials.ebitda)}</strong></span> : null}
                        {opp.ebitdaMultiple  ? <span>{t.multiple}: <strong className="text-foreground">{opp.ebitdaMultiple.toFixed(1)}×</strong></span> : null}
                      </div>
                    )}
                  </div>
                  {opp.ndaSigned && (
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-medium" style={{ color: NAVY }}>{formatCurrency(opp.financials?.askingPrice ?? 0)}</p>
                      <p className="text-[10px] text-muted-foreground">{t.askingPrice}</p>
                    </div>
                  )}
                  {opp.ndaSigned ? (
                    <Button
                      size="sm"
                      className="flex-shrink-0 text-[11px] h-7 px-3"
                      onClick={() => setPreScreenDealId(opp.dealId)}
                      style={{ background: NAVY, color: GOLD, border: "1px solid rgba(206,171,101,0.25)" }}
                    >
                      {t.reviewSubmit}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-shrink-0 text-[11px] h-7 px-3 gap-1"
                      onClick={() => handleSignNda(opp)}
                    >
                      <PenLine className="w-3 h-3" />
                      {t.signNda}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
          {filteredOpportunities.length > 0 && (
            <div className="pt-3 mt-1 border-t border-border">
              <button
                className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors ml-auto"
                onClick={() => navigate("/dashboard/lender/loan-offers")}
              >
                {t.viewAllOpportunities} <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card style={{ border: "0.5px solid var(--color-border-tertiary)" }}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: GOLD }} />
            {t.quickActions}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2.5">
            <QuickActionCard icon={Building2}     label={t.browseDeals}  onClick={() => navigate("/dashboard/lender/loan-offers")} />
            <QuickActionCard icon={Monitor}       label={t.dealMonitor}  onClick={() => navigate("/listings")} />
            <QuickActionCard icon={BarChart3}     label={t.pipeline}     onClick={() => navigate("/dashboard/lender/pipeline")} />
            <QuickActionCard icon={CalendarIcon}  label={t.calendar}     onClick={() => navigate("/dashboard/lender/calendar")} />
            <QuickActionCard icon={Video}         label={t.videoConf}    onClick={() => navigate("/dashboard/lender/video")} />
            <QuickActionCard icon={FolderOpen}    label={t.documents}    onClick={() => navigate("/dashboard/lender/documents")} />
            <QuickActionCard icon={MessageSquare} label={t.messages}     onClick={() => navigate("/dashboard/lender/messages")} />
            <QuickActionCard icon={Users}         label={t.consultants}  onClick={() => navigate("/dashboard/lender/consultants")} />
            <QuickActionCard icon={FileText}      label={t.submissions}  onClick={() => navigate("/lender/submit-offer")} />
          </div>
        </CardContent>
      </Card>

      {/* My submitted offers */}
      {user && <MyOffersSection userId={user.id} />}

      {/* Meetings */}
      <Card style={{ border: "0.5px solid var(--color-border-tertiary)" }}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: GOLD }} />
            <Video className="w-4 h-4 text-muted-foreground" />
            {t.recentMeetings}
          </CardTitle>
          <CardDescription className="text-xs">{t.meetingsDesc}</CardDescription>
        </CardHeader>
        <CardContent>
          <DealMeetingsSection />
        </CardContent>
      </Card>
    </div>
  );
}